<template>
  <div class="administrar-biomasa-eventos container-fluid mt-4">
    <h2><font-awesome-icon :icon="['fas', 'leaf']" class="me-1 text-success" /> Biomasa de este Evento</h2>

    <!-- Filtros y acciones -->
    <div class="filters my-3 d-flex flex-wrap gap-2 align-items-center">
      <input v-model="filterTipo" type="text" class="form-control form-control-sm" placeholder="Filtrar por tipo..."
        style="max-width: 200px;" />
      <input v-model="filterFechaInicio" type="date" class="form-control form-control-sm" style="max-width: 180px;" />
      <input v-model="filterFechaFin" type="date" class="form-control form-control-sm" style="max-width: 180px;" />
      <div class="form-check form-switch ms-2">
        <input class="form-check-input" type="checkbox" id="hideSalida" v-model="hideSalida" />
        <label class="form-check-label small" for="hideSalida">
          Ocultar con fecha de salida
        </label>
      </div>
      <button class="btn btn-secondary btn-sm ms-auto" @click="clearFilters">
        Limpiar Filtros
      </button>
    </div>

    <!-- Tabla con ordenamiento y paginación -->
    <div v-if="loading" class="text-center my-3">Cargando datos...</div>
    <div v-else>
      <div class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="evt in paginatedEventos" :key="evt.id">
          <div class="card h-100 shadow-sm" :class="{ 'bg-light': evt.fecha_fin }">
            <div class="card-body d-flex flex-column justify-content-between">
              <div>
                <h5 class="card-title mb-1">
                  Biomasa: {{ evt.biomasa.tipo_biomasa }}
                </h5>
                <p class="mb-1 text-muted small">ID: #{{ evt.id }}</p>
                <p class="mb-1"><strong>Ingreso:</strong> {{ formatDate(evt.fecha_inicio) }}</p>
                <p class="mb-1"><strong>Salida:</strong> {{ formatDate(evt.fecha_fin) || '—' }}</p>
                <p class="mb-1"><strong>Lote:</strong>
                  <span v-if="hasLote(evt)">
                    {{ evt.biomasa.planta_biomasa[0].planta.lote.nombre }}
                    <small v-if="evt.biomasa.planta_biomasa[0].planta.lote.codigo">
                      ({{ evt.biomasa.planta_biomasa[0].planta.lote.codigo }})
                    </small>
                  </span>
                  <span v-else>—</span>
                </p>
                <p class="small text-muted mb-1">
                  {{ evt.observaciones || 'Sin comentarios' }}
                </p>
              </div>
              <div class="mt-2 text-end">
                <button class="btn btn-sm btn-outline-primary" @click="editEvento(evt)" :disabled="evt.fecha_fin">
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!filteredEventos.length" class="text-center text-muted">
        No hay registros.
      </div>
    </div>

    <!-- Paginación -->
    <nav v-if="totalPages > 1" class="mt-3">
      <ul class="pagination pagination-sm justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="prevPage">Anterior</a>
        </li>
        <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: currentPage === p }">
          <a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="nextPage">Siguiente</a>
        </li>
      </ul>
    </nav>

    <!-- Formulario Editar Ingreso/Salida -->
    <div v-if="showForm" class="card shadow-sm p-3 my-3">
      <h5>Editar Evento de Biomasa</h5>
      <form @submit.prevent="submitForm">

        <div class="mb-2 mt-2">
          <label class="form-label mb-0 small">Observaciones:</label>
          <textarea v-model="form.observaciones" class="form-control form-control-sm" rows="2"></textarea>
        </div>
        <div class="d-flex gap-2">
          <button type="submit" class="btn btn-success btn-sm">Guardar</button>
          <button type="button" class="btn btn-secondary btn-sm" @click="cancelForm">Cancelar</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import { mapGetters } from 'vuex';

export default {
  name: 'ListarBiomasaEvento',
  props: {
    proyecto_id: { type: Number, required: true },
    proyecto_evento_id: { type: Number, required: true }
  },
  data() {
    return {
      eventos: [],
      loading: false,
      filterTipo: '',
      filterFechaInicio: '',
      filterFechaFin: '',
      hideSalida: true,
      currentPage: 1,
      pageSize: 10,
      showForm: false,
      form: { id: null, fecha_inicio: '', fecha_fin: '', observaciones: '' },
      sortKey: 'id',
      sortAsc: true,
      club_id: this.$store.getters.getClubActual.id
    };
  },
  computed: {
    ...mapGetters(['getClubActual']),
    filteredEventos() {
      let list = this.eventos;
      if (this.hideSalida) {
        list = list.filter(ev => !ev.fecha_fin);
      }
      if (this.filterTipo) {
        list = list.filter(ev =>
          ev.biomasa.tipo_biomasa.toLowerCase().includes(this.filterTipo.toLowerCase())
        );
      }
      if (this.filterFechaInicio) {
        list = list.filter(ev =>
          ev.fecha_inicio && new Date(ev.fecha_inicio).toISOString().slice(0, 10) === this.filterFechaInicio
        );
      }
      if (this.filterFechaFin) {
        list = list.filter(ev =>
          ev.fecha_fin && new Date(ev.fecha_fin).toISOString().slice(0, 10) === this.filterFechaFin
        );
      }
      // ordenar
      list.sort((a, b) => {
        let va = a[this.sortKey] || '';
        let vb = b[this.sortKey] || '';
        if (this.sortKey.includes('fecha')) {
          va = va ? new Date(va) : new Date(0);
          vb = vb ? new Date(vb) : new Date(0);
        }
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });
      // eventos con fecha_fin al final
      const sinSalida = list.filter(ev => !ev.fecha_fin);
      const conSalida = list.filter(ev => ev.fecha_fin);
      return [...sinSalida, ...conSalida];
    },
    paginatedEventos() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredEventos.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.filteredEventos.length / this.pageSize) || 1;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    }
  },
  mounted() {
    this.fetchEventos();
  },
  methods: {
    hasLote(evt) {
      return evt.biomasa && evt.biomasa.planta_biomasa &&
        evt.biomasa.planta_biomasa.length &&
        evt.biomasa.planta_biomasa[0].planta &&
        evt.biomasa.planta_biomasa[0].planta.lote;
    },
    async fetchEventos() {
      this.loading = true;
      try {
        const res = await axios.get('/api/biomasa-eventos', {
          params: { club_id: this.club_id, proyecto_id: this.proyecto_id, proyecto_evento_id: this.proyecto_evento_id }
        });
        this.eventos = res.data;
      } catch (e) {
        console.error('Error obteniendo eventos:', e);
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString();
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    showCreateForm() {
      // no crea, solo edita fecha ingresada/salida
    },
    editEvento(evt) {
      this.form = {
        id: evt.id,
        fecha_inicio: evt.fecha_inicio ? new Date(evt.fecha_inicio).toISOString().slice(0, 10) : '',
        fecha_fin: evt.fecha_fin ? new Date(evt.fecha_fin).toISOString().slice(0, 10) : '',
        observaciones: evt.observaciones || ''
      };
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
    },
    async submitForm() {
      try {
        await axios.put(`/api/biomasa-eventos/${this.form.id}`, this.form, { params: { club_id: this.club_id } });
        alert('Evento de biomasa actualizado.');
        this.showForm = false;
        this.fetchEventos();
      } catch (e) {
        console.error('Error al guardar evento:', e);
        alert('Error al guardar evento');
      }
    },
    deleteEvento() {
      // no permitido
    },
    clearFilters() {
      this.filterTipo = '';
      this.filterFechaInicio = '';
      this.filterFechaFin = '';
      this.currentPage = 1;
      this.hideSalida = true;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    goToPage(p) {
      this.currentPage = p;
    }
  }
};
</script>

<style scoped>
.administrar-biomasa-eventos {
  padding: 15px;
  background-color: #f7f8fa;
}

.filters input {
  max-width: 200px;
}

.filters button {
  height: calc(1.5em + .75rem + 2px);
}

.cursor-pointer {
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
}

.table-danger td {
  color: #842029;
  background-color: #f8d7da;
}

.card {
  transition: transform 0.2s ease;
}

.card:hover {
  transform: scale(1.02);
}

.card .card-title {
  color: #28a745;
  font-weight: 600;
}

.bg-light {
  background-color: #f5f5f5 !important;
}
</style>